export interface CoCRequest {
  applicationUid: string;
  type: keyof typeof CoCRequestType;
  status: GUESTApplicationStatus;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  daysYachtService: number;
  monthsYachtService: number;
  daysGuest: number;
  monthsGuest: number;
  daysMaritimeService: number;
  monthsMaritimeService: number;
  daysSeaService: number;
  applicationDocuments: ApplicationDocument[];
  certificateUid?: string;
  firstPaymentUid?: string;
  secondPaymentUid?: string;
}

export interface ApplicationDocument {
  uid: string;
  applicactionUid: string;
  documentUid: string;
  documentType: ApplicationDocumentType;
}

export interface ResponseApplicationDocument {
  uid: string;
  applicactionUid: string;
  documentUid: string;
  documentType: ApplicationDocumentType;
}

export interface ApplicationComment {
  uid: string;
  body: string;
  createdAt: string;
}

export type ApplicationRequirementsType = 'ALL_REQUIRED' | number;

export enum CoCRequestType {
  JUNIOR_STEWARD = 'Junior Steward/Stewardess',
  HEAD_HOUSEKEEPING = 'Yacht Head of Housekeeping',
  HEAD_SERVICES = 'Yacht Head of Services',
  CHIEF_STEWARD = 'Chief Steward/Stewardess',
  SPA_MANAGEMENT = 'Yacht Spa Management',
  PURSER = 'Purser',
  SENIOR_STEWARD = 'Yacht Senior Steward/Stewardess',
}

export enum ApplicationDocumentType {
  SST_12M = 'Verified Senior role including 12 mths Sea Service & 60 guest day',
  SST_24MB = 'Verified 24 mths Sea Service & 120 guest days',
  SST_24MA = 'Verified over 12 mths Sea Service & 30 guest days',
  SST_12MY = 'Verified over 12 mths Yacht Service with 30 guest days',
  SST_3CV = 'Verified 3 seasons on a Charter Vessel with 30 guest days',
  SST_10Y = 'Verified 10 mths Yacht Service & 60 guest days from issue date of GUEST Senior or Head of Department CoC',
  STWC_TC = 'STCW95 training courses x 5',
  STWC_BST = 'STCW A-V1/1 Basic Safety Training',
  STWC_PSA = 'STCW Proficiency in Security Awareness',
  STCW_CMT = 'STCW (V/2, A-V/2) Crowd Management Training',
  STCW_MCC = 'STCW (A-VI/4-2) Medical Care Course',
  STCW_MFA = 'STCW (A-VI/4-1) Medical First Aid',
  STCW_AFF = 'STCW (A-VI/3) Advanced Fire Fighting',
  STCW_ASS = 'STCW (A-VI/2-1) PSC&RB or Advanced Sea Survival',
  RYA_PB = 'RYA Powerboat Level 2 or Tender Driver License',
  BTQ = 'Recognised Beauty Therapist Qualifications',
  MH_FIRST_AID = 'Mental Health First Aid Course',
  MH_AWARENESS = 'Mental Health Awareness Course',
  YRC = 'MCA Yacht Rating Certificate',
  SAFETY_MCF = 'International Safety Management Code Familiarization',
  GUEST_U18 = 'GUEST Yacht Purser Program Units 18 - 21',
  GUEST_U30 = 'GUEST Yacht Human Resources & Crew Wellbeing Unit 30',
  GUEST_U31 = 'GUEST Spa Management Unit 31',
  GUEST_U23 = 'GUEST Introduction to Yachting & Yachting Life Unit 23',
  GUEST_U09 = 'GUEST Interior Valet Services Unit 09',
  GUEST_U10 = 'GUEST Floristy & Plant Maintenance Unit 10',
  GUEST_U11 = 'GUEST Barista & Hot Beverages Unit 11',
  GUEST_U14 = 'GUEST Advanced Bartending & Mixology 1 Unit 14',
  GUEST_U07 = 'GUEST Interior Advanced Laundry Service Unit 07',
  GUEST_U08 = 'GUEST Interior Advanced Housekeeping Unit 08',
  GUEST_U05 = 'GUEST Interior Advanced Food & Beverage Service Unit 05',
  GUEST_U04 = 'GUEST Interior Administrations Unit 34 (or Unit 04)',
  GUEST_U22 = 'GUEST IAMI Advanced Leadership Unit 22 or equivalent',
  GUEST_U17 = 'GUEST Foundation Leadership Unit 17',
  GUEST_U36 = 'GUEST Destination, Event & Interior Management Unit 36',
  GUEST_U06 = 'GUEST Cigar Service Unit 06',
  GUEST_U03 = 'GUEST Basic Wine Bartending & Mixology Unit 03',
  GUEST_U32 = 'GUEST Basic Housekeeping & Laundry Unit 32',
  GUEST_U02 = 'GUEST Basic Food Service Unit 02',
  GUEST_U12 = 'GUEST Advanced Wine Appreciation Unit 12 or WSET equivalent',
  GUEST_U21 = 'GUEST Accounting, Bookkeeping & Budgeting Unit 21  or equivalent qualification or experience',
  FOOD_SAFETY = 'Food Safety/Hygiene level 2 in Catering or equivalent',
  MMS = 'Evidence of 3 years Management role in Maritime Service',
  ITRB = 'Completed Interior Training Record Book',
  YTRB = 'Yacht Interior Training Record Book',
  ABC = 'Accounting & Bookkeeping Course',
  STCW_DSD = 'STCW (A-V1/6-2) Proficiency in Designated Security Duties',
  CV = 'CV',
  PASSPORT = 'PASSPORT',
  PASSPORT_PHOTO = 'PASSPORT_PHOTO',
  STCW_1 = 'STCW-1',
  STCW_2 = 'STCW-2',
  STCW_3 = 'STCW-3',
  STCW_4 = 'STCW-4',
  STCW_5 = 'STCW-5',
}

export const documentsTypes = {
  [CoCRequestType.JUNIOR_STEWARD]: {
    coursesCompleted: [
      ApplicationDocumentType.STWC_BST,
      ApplicationDocumentType.STWC_PSA,
      ApplicationDocumentType.FOOD_SAFETY,
    ],
    coursesCompletedRequired: 'ALL_REQUIRED',
    requiredForApplication: [
      ApplicationDocumentType.GUEST_U23,
      ApplicationDocumentType.GUEST_U32,
      ApplicationDocumentType.GUEST_U02,
      ApplicationDocumentType.GUEST_U03,
    ],
    oneOfDocsRequired: [],
    stwcCoursesRequired: 2,
  },
  [CoCRequestType.HEAD_HOUSEKEEPING]: {
    coursesCompleted: [
      ApplicationDocumentType.GUEST_U10,
      ApplicationDocumentType.MH_AWARENESS,
      ApplicationDocumentType.STCW_MFA,
      ApplicationDocumentType.RYA_PB,
      ApplicationDocumentType.YTRB,
      ApplicationDocumentType.YRC,
    ],
    coursesCompletedRequired: 2,
    requiredForApplication: [
      ApplicationDocumentType.GUEST_U04,
      ApplicationDocumentType.GUEST_U07,
      ApplicationDocumentType.GUEST_U08,
      ApplicationDocumentType.GUEST_U09,
    ],
    oneOfDocsRequired: [
      ApplicationDocumentType.SST_24MA,
      ApplicationDocumentType.SST_3CV,
    ],
    stwcCoursesRequired: 5,
  },
  [CoCRequestType.HEAD_SERVICES]: {
    coursesCompleted: [
      ApplicationDocumentType.GUEST_U11,
      ApplicationDocumentType.GUEST_U06,
      ApplicationDocumentType.GUEST_U10,
      ApplicationDocumentType.MH_AWARENESS,
      ApplicationDocumentType.YTRB,
      ApplicationDocumentType.STCW_MFA,
      ApplicationDocumentType.RYA_PB,
      ApplicationDocumentType.YRC,
    ],
    coursesCompletedRequired: 2,
    requiredForApplication: [
      ApplicationDocumentType.GUEST_U04,
      ApplicationDocumentType.GUEST_U05,
      ApplicationDocumentType.GUEST_U12,
      ApplicationDocumentType.GUEST_U14,
      ApplicationDocumentType.FOOD_SAFETY,
    ],
    oneOfDocsRequired: [
      ApplicationDocumentType.SST_12MY,
      ApplicationDocumentType.SST_3CV,
    ],
    stwcCoursesRequired: 5,
  },
  [CoCRequestType.CHIEF_STEWARD]: {
    coursesCompleted: [
      ApplicationDocumentType.ABC,
      ApplicationDocumentType.MH_FIRST_AID,
      ApplicationDocumentType.STCW_DSD,
      ApplicationDocumentType.STCW_CMT,
      ApplicationDocumentType.STCW_MCC,
      ApplicationDocumentType.STCW_AFF,
      ApplicationDocumentType.STCW_ASS,
      ApplicationDocumentType.SAFETY_MCF,
    ],
    coursesCompletedRequired: 2,
    requiredForApplication: [
      ApplicationDocumentType.GUEST_U36,
      ApplicationDocumentType.GUEST_U30,
      ApplicationDocumentType.GUEST_U17,
      ApplicationDocumentType.FOOD_SAFETY,
    ],
    oneOfDocsRequired: [
      ApplicationDocumentType.SST_24MB,
      ApplicationDocumentType.SST_10Y,
    ],
    stwcCoursesRequired: 2,
  },
  [CoCRequestType.SPA_MANAGEMENT]: {
    coursesCompleted: [
      ApplicationDocumentType.STWC_BST,
      ApplicationDocumentType.STWC_PSA,
      ApplicationDocumentType.BTQ,
    ],
    coursesCompletedRequired: 'ALL_REQUIRED',
    requiredForApplication: [
      ApplicationDocumentType.GUEST_U04,
      ApplicationDocumentType.GUEST_U31,
      ApplicationDocumentType.GUEST_U17,
    ],
    oneOfDocsRequired: [],
    stwcCoursesRequired: 2,
  },
  [CoCRequestType.PURSER]: {
    coursesCompleted: [
      ApplicationDocumentType.SST_12M,
      ApplicationDocumentType.MMS,
    ],
    coursesCompletedRequired: 1,
    requiredForApplication: [
      ApplicationDocumentType.GUEST_U18,
      ApplicationDocumentType.GUEST_U22,
    ],
    oneOfDocsRequired: [],
    stwcCoursesRequired: 2,
  },
  [CoCRequestType.SENIOR_STEWARD]: {
    coursesCompleted: [
      ApplicationDocumentType.GUEST_U11,
      ApplicationDocumentType.GUEST_U14,
      ApplicationDocumentType.GUEST_U10,
      ApplicationDocumentType.GUEST_U12,
      ApplicationDocumentType.GUEST_U06,
      ApplicationDocumentType.MH_AWARENESS,
      ApplicationDocumentType.STCW_MFA,
      ApplicationDocumentType.RYA_PB,
      ApplicationDocumentType.ITRB,
      ApplicationDocumentType.YRC,
    ],
    coursesCompletedRequired: 3,
    requiredForApplication: [
      ApplicationDocumentType.GUEST_U04,
      ApplicationDocumentType.GUEST_U05,
      ApplicationDocumentType.GUEST_U07,
      ApplicationDocumentType.GUEST_U08,
      ApplicationDocumentType.GUEST_U09,
      ApplicationDocumentType.FOOD_SAFETY,
    ],
    oneOfDocsRequired: [
      ApplicationDocumentType.SST_24MA,
      ApplicationDocumentType.SST_3CV,
    ],
    stwcCoursesRequired: 0,
  },
};

export enum GUESTApplicationStatus {
  DOCUMENTS_REVISION = 'DOCUMENTS_REVISION',
  APPLICATION_PAYMENT_SUCCESS = 'APPLICATION_PAYMENT_SUCCESS',
  DOCUMENTS_UPLOADED = 'DOCUMENTS_UPLOADED',

  CERTIFICATE_PAYMENT_FAILED = 'CERTIFICATE_PAYMENT_FAILED',
  CERTIFICATE_PAYMENT_SUCCESS = 'CERTIFICATE_PAYMENT_SUCCESS',
  CERTIFICATE_PAYMENT_PROCESSING = 'CERTIFICATE_PAYMENT_PROCESSING',
  ISSUED = 'ISSUED',
  APPROVED = 'APPROVED',

  REJECTED = 'REJECTED',
}

export interface GetGUESTApplicationsResponse {
  data: CoCRequest[];
}

export interface GetGUESTApplicationByIdRequest {
  applicationUid: string;
  authToken?: string;
}

export interface GetGUESTApplicationByIdResponse {
  data: CoCRequest;
}

export interface GetGUESTApplicationDocumentsRequest {
  applicationUid: string;
  authToken?: string;
}

export interface GetGUESTApplicationDocuments {
  data: ResponseApplicationDocument[];
}

export interface UploadGUESTApplicationDocument {
  uid: string;
  applicationUid: string;
  document: FormData;
  authToken: string;
}

export interface GetGUESTApplicationImage {
  applicationUid: string;
  authToken: string;
}

export interface ChangeGUESTApplicationStatus {
  uid: string;
  status: GUESTApplicationStatus;
  authToken?: string;
}

export interface GetGUESTApplicationComments {
  data: ApplicationComment[];
}

export interface AddGUESTApplicationComment {
  uid: string;
  comment: string;
}
